import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ApplicationContextService } from '../services/application-context.service';
import { ApplicationConfigService } from '../services/application-config.service';

@UntilDestroy({ checkProperties: true })
@Component({
    template: '' // This is a base class, no template
})
export abstract class SmartSubTabBaseComponent implements OnInit, OnDestroy {

    @Input() parentTabName!: string; // e.g., 'Devices', 'Master'

    selectedTabIndex: number = 0;
    allSubTabs: string[] = [];
    availableSubTabs: string[] = [];
    selectedSubTab: string = '';

    constructor(
        protected applicationContextService: ApplicationContextService,
        protected applicationConfigService: ApplicationConfigService
    ) { }

    ngOnInit(): void {
        if (!this.parentTabName) {
            console.error('SmartSubTabBaseComponent: parentTabName is required');
            return;
        }

        this.initializeSubTabs();
        this.subscribeToApplicationContext();
    }

    ngOnDestroy(): void {
        // Handled by @UntilDestroy decorator
    }

    private initializeSubTabs(): void {
        this.allSubTabs = this.applicationConfigService.getDefaultSubTabsFor(this.parentTabName);
        this.availableSubTabs = [...this.allSubTabs];
        this.selectedSubTab = this.availableSubTabs[this.selectedTabIndex] || '';
    }

    private subscribeToApplicationContext(): void {
        this.applicationContextService.selectedApplicationId$
            .pipe(untilDestroyed(this))
            .subscribe(applicationId => {
                this.updateAvailableSubTabs(applicationId);
            });
    }

    private updateAvailableSubTabs(applicationId: number | null): void {
        if (applicationId) {
            this.availableSubTabs = this.applicationConfigService.getAvailableSubTabsFor(applicationId, this.parentTabName);
        } else {
            this.availableSubTabs = [...this.allSubTabs];
        }

        // Reset to first tab if current tab is not available
        if (this.selectedTabIndex >= this.availableSubTabs.length) {
            this.selectedTabIndex = 0;
        }
        this.selectedSubTab = this.availableSubTabs[this.selectedTabIndex] || '';

        console.log(`${this.parentTabName} sub-tabs updated:`, this.availableSubTabs);
    }

    onSubTabChange(index: number): string | undefined {
        if (typeof index === 'number' && index >= 0 && index < this.availableSubTabs.length) {
            this.selectedTabIndex = index;
            this.selectedSubTab = this.availableSubTabs[index];
            console.log(`${this.parentTabName} sub-tab changed to:`, this.selectedSubTab);
            return this.selectedSubTab;
        } else {
            console.error(`Invalid ${this.parentTabName} sub-tab index:`, index);
            return undefined;
        }
    }

    getSubTabComponentIndex(tabIndex: number): number {
        const subTabName = this.availableSubTabs[tabIndex];
        return this.allSubTabs.indexOf(subTabName);
    }

    isSubTabAvailable(subTabName: string): boolean {
        return this.availableSubTabs.includes(subTabName);
    }
}